import { useViewContext } from "@folksam/ui/providers/ViewContext";
import React from "react";

import { Checkbox } from "../../atoms";
import { Box, Flex } from "../../cores";
import {
  IconCheckmark,
  IconCo2,
  IconColoredNumber,
  IconColoredNumberOneToFive,
  IconEuroNCAP,
  IconFargBetyg,
  IconGoodChooice,
  IconPumpScore,
  IconStarNeg,
} from "../../icons";
import { IKeyStringAndValue } from "../../types/Types";
import Table from "./Table";

interface ITableColumnCustom {
  element: Array<string>;
  index: number;
  selected: Array<IKeyStringAndValue>;
  setSelected: React.Dispatch<React.SetStateAction<Array<IKeyStringAndValue>>>;
  filteredData: Array<IKeyStringAndValue>;
  rowIndex: number;
  currentPage: number | string;
  PageSize: number;
  filterOptions?: IKeyStringAndValue;
}
const TableColumnCustom = ({
  element,
  index,
  selected,
  setSelected,
  filteredData,
  rowIndex,
  currentPage,
  PageSize,
  filterOptions,
}: ITableColumnCustom) => {
  const { isMobile } = useViewContext();
  const newIndex = rowIndex + (currentPage as number) * PageSize - PageSize;
  const emptyFilterOptions = Object.keys(filterOptions || {}).length === 0;
  const isChecked = selected.some(
    (element) =>
      JSON.stringify(element) === JSON.stringify(filteredData[newIndex])
  )
    ? true
    : false;

  const handleChange = () => {
    if (!isChecked) {
      setSelected([...selected, filteredData[newIndex]]);
    } else {
      const filteredArray = selected.filter(
        (e) => JSON.stringify(e) !== JSON.stringify(filteredData[newIndex])
      );
      setSelected([...filteredArray]);
    }
  };

  const TableColumnWithFlex = (component: JSX.Element) => {
    return (
      <Table.Column key={index}>
        <Flex
          sx={{
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {component}
        </Flex>
      </Table.Column>
    );
  };
  const TableColumnWithoutFlex = (component: JSX.Element) => {
    return <Table.Column key={index}>{component}</Table.Column>;
  };

  const ColoredNumber = () => {
    return <IconColoredNumber value={element[1]} />;
  };
  const ColoredNumberOneToFive = () => {
    return <IconColoredNumberOneToFive value={element[1]} />;
  };
  const EuroNCAP = () => {
    return <IconEuroNCAP value={element[1]} key={index} />;
  };
  const GoodChooice = () => {
    return <IconGoodChooice />;
  };
  const Checkmark = () => {
    return <IconCheckmark />;
  };
  const Co2 = () => {
    return <IconCo2 value={element[1]} />;
  };
  const FargBetyg = () => {
    return <IconFargBetyg value={element[1]} />;
  };

  const PumpScore = (noColor: boolean, green: boolean) => {
    return <IconPumpScore noColor={noColor} green={green} value={element[1]} />;
  };

  const PumpCase = (
    compareNumberLL: number,
    compareNumberVV: number,
    compareNumberLV: number
  ) => {
    const effektivitetNumber = parseFloat(element[1]);
    return !emptyFilterOptions
      ? (filterOptions as IKeyStringAndValue)["typeOfPump"] !== "fl"
        ? (filterOptions as IKeyStringAndValue)["typeOfPump"] === "ll"
          ? TableColumnWithFlex(
              PumpScore(false, effektivitetNumber >= compareNumberLL)
            )
          : (filterOptions as IKeyStringAndValue)["typeOfPump"] === "vv"
          ? TableColumnWithFlex(
              PumpScore(false, effektivitetNumber >= compareNumberVV)
            )
          : (filterOptions as IKeyStringAndValue)["typeOfPump"] === "lv"
          ? TableColumnWithFlex(
              PumpScore(false, effektivitetNumber >= compareNumberLV)
            )
          : null
        : TableColumnWithFlex(PumpScore(true, false))
      : TableColumnWithFlex(PumpScore(true, false));
  };

  const SpecPumpCase = () => {
    return !emptyFilterOptions
      ? (filterOptions as IKeyStringAndValue)["typeOfPump"] === "fl"
        ? TableColumnWithFlex(PumpScore(true, false))
        : TableColumnWithFlex(PumpScore(false, false))
      : TableColumnWithFlex(PumpScore(true, false));
  };

  const DefaultTableColum = () => {
    return (
      <Table.Column key={index}>
        <Flex
          sx={{
            height: "100%",
            alignItems: "center",
            textAlign: index === 0 ? "left" : "center",
          }}
        >
          {index === 0 && !("omraden" in Object.values(filteredData)[0]) ? (
            <Checkbox
              key={newIndex}
              checked={isChecked}
              id={element[1] + newIndex}
              onChange={handleChange}
              //@ts-ignore Incorrect type in Folksam UI
              testId={element[1]}
            />
          ) : null}
          <Flex
            sx={{
              width: "100%",
              justifyContent: index === 0 ? "flex-start" : "center",
            }}
          >
            <Box
              as={index === 0 ? "label" : "span"}
              htmlFor={index === 0 ? element[1] + newIndex : undefined}
              sx={
                index === 0
                  ? {
                      height: "auto",
                      marginLeft: isMobile ? "8px" : "12px",
                    }
                  : null
              }
            >
              {element[1]}
            </Box>
          </Flex>
        </Flex>
      </Table.Column>
    );
  };

  const setTableColumn: { [key: string]: JSX.Element | null } = {
    safetygradenumber: TableColumnWithFlex(ColoredNumber()),
    safetygrade: TableColumnWithFlex(ColoredNumber()),
    whiplash: TableColumnWithFlex(ColoredNumber()),
    autobreak: TableColumnWithFlex(ColoredNumber()),
    airbag_passenger: TableColumnWithFlex(ColoredNumber()),
    side_curtain: TableColumnWithFlex(ColoredNumber()),
    sidecurtain: TableColumnWithFlex(ColoredNumber()),
    ldw_lka: TableColumnWithFlex(ColoredNumber()),
    "LDW/LKA": TableColumnWithFlex(ColoredNumber()),
    autobreak_car: TableColumnWithFlex(ColoredNumber()),
    autobreak_pedestrian: TableColumnWithFlex(ColoredNumber()),
    autobreak_bike: TableColumnWithFlex(ColoredNumber()),
    antispin: TableColumnWithFlex(ColoredNumber()),
    anti_slip: TableColumnWithFlex(ColoredNumber()),
    kid_safety: TableColumnWithFlex(ColoredNumber()),
    folksam_result: TableColumnWithFlex(ColoredNumberOneToFive()),
    Braval:
      element[1] === "1" ? (
        TableColumnWithFlex(GoodChooice())
      ) : (
        <Table.Column></Table.Column>
      ),
    good_choise:
      element[1] === "J" ? (
        TableColumnWithFlex(GoodChooice())
      ) : (
        <Table.Column></Table.Column>
      ),
    safest_choice:
      element[1] === "J" ? (
        TableColumnWithFlex(GoodChooice())
      ) : (
        <Table.Column></Table.Column>
      ),
    "climate-bonus": TableColumnWithoutFlex(
      element[1] === "0" ? <></> : Checkmark()
    ),
    climate_bonus_car: TableColumnWithoutFlex(
      element[1] === "0" ? <></> : Checkmark()
    ),
    euroncap: TableColumnWithoutFlex(EuroNCAP()),
    pv_cycle: TableColumnWithoutFlex(element[1] === "0" ? <></> : Checkmark()),
    co2: TableColumnWithoutFlex(Co2()),
    alnarp: TableColumnWithoutFlex(FargBetyg()),
    boras: TableColumnWithoutFlex(FargBetyg()),
    uppsala: TableColumnWithoutFlex(FargBetyg()),
    vindeln: TableColumnWithoutFlex(FargBetyg()),
    boras_betyg: TableColumnWithoutFlex(FargBetyg()),
    alnarp_betyg: TableColumnWithoutFlex(FargBetyg()),
    uppsala_betyg: TableColumnWithoutFlex(FargBetyg()),
    vindeln_betyg: TableColumnWithoutFlex(FargBetyg()),
    funktion: TableColumnWithoutFlex(FargBetyg()),
    miljo_halsa: TableColumnWithoutFlex(FargBetyg()),
    riksbetyg: TableColumnWithoutFlex(FargBetyg()),
    Effektivitet: PumpCase(12.5, 9, 8),
    Miljo: PumpCase(5, 6, 7),
    Garanti: SpecPumpCase(),
    Funktion: SpecPumpCase(),
  };

  if (element !== null) {
    return setTableColumn[element[0]] ?? DefaultTableColum();
  } else return <></>;
};
export default TableColumnCustom;
